import { useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table, { Column } from '../../components/Table';

const REPORTS = [
  { key: 'monthly-summary', label: 'Monthly Payroll Summary', filter: 'month' },
  { key: 'cpf-return', label: 'CPF Return', filter: 'month' },
  { key: 'yearly-summary', label: 'Yearly Summary Report', filter: 'employeeYear' },
  { key: 'allowance-report', label: 'Allowance Report', filter: 'month' },
  { key: 'employee-list', label: 'Employee List', filter: 'type' },
] as const;

export default function PayrollReports() {
  const [active, setActive] = useState<(typeof REPORTS)[number]>(REPORTS[0]);
  const [month, setMonth] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [type, setType] = useState('active');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const buildParams = () => {
    if (active.filter === 'month') return { month: month || undefined };
    if (active.filter === 'employeeYear') return { employeeId, year };
    if (active.filter === 'type') return { type };
    return {};
  };

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll/reports/${active.key}`, { params: buildParams() });
      setRows(res.data.rows);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const res = await api.get(`/payroll/reports/${active.key}`, { params: { ...buildParams(), format: 'excel' }, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${active.key}.xlsx`;
    a.click();
  };

  const columns: Column<any>[] =
    rows.length > 0
      ? Object.keys(rows[0]).map((k) => ({ key: k, header: k.replace(/_/g, ' ').toUpperCase() }))
      : [];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Payroll Reports</h1>
      <div className="flex gap-2 mb-4 border-b overflow-x-auto">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            onClick={() => {
              setActive(r);
              setRows([]);
            }}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${active.key === r.key ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap items-end gap-3">
        {active.filter === 'month' && <Input label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value + '-01')} />}
        {active.filter === 'employeeYear' && (
          <>
            <Input label="Employee ID" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
            <Input label="Year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          </>
        )}
        {active.filter === 'type' && (
          <Select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'new-join', label: 'New Join (this month)' },
              { value: 'resigned', label: 'Resigned' },
            ]}
          />
        )}
        <Button onClick={run} loading={loading}>
          Preview
        </Button>
        <Button variant="secondary" onClick={exportExcel}>
          Export to Excel
        </Button>
      </div>

      <Table columns={columns} rows={rows} loading={loading} emptyMessage="Click Preview to run the report" />
    </div>
  );
}
